/**
 * OrderTable Component
 * 
 * Displays orders in a table format with sorting, filtering, and actions.
 * Used in the Order Management screen for managers.
 * 
 * @module OrderTable
 */

import React, { useState, useMemo, useCallback, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import {
  CheckCircle,
  Clock,
  Eye,
  Flag,
  MoreVertical,
  FileText,
  FileSpreadsheet,
  Send,
  Edit,
  Trash2
} from 'lucide-react';
import { Order, OrderStatus } from '../../@types/order';
import { formatOrderDateForDisplay, orderDateToLocalSortable, parseCalendarYMDToLocalDate } from '../../utils/calendarDate';
import { ThemeColors, Spacing, Typography, Shadows } from '../../styles/ThemeStyles';
import { Timestamp } from 'firebase/firestore';
import { SortableFilterableTableHeader, ColumnFilter, ColumnSort } from '../common/SortableFilterableTableHeader';

/**
 * OrderTable props interface
 */
export interface OrderTableProps {
  /** Orders to display */
  orders: Order[];
  
  /** Loading state */
  isLoading?: boolean;
  
  /** Handle row click */
  onRowClick?: (order: Order) => void;
  
  /** Handle PDF download */
  onDownloadPDF?: (order: Order) => void;
  
  /** Handle CSV download */
  onDownloadCSV?: (order: Order) => void;
  
  /** Handle view action */
  onView?: (order: Order) => void;
  
  /** Handle flag action */
  onFlag?: (order: Order) => void;
  
  /** Handle approve action */
  onApprove?: (order: Order) => void;
  
  /** Handle edit action */
  onEdit?: (order: Order) => void;
  
  /** Handle delete action */
  onDelete?: (order: Order) => void;
  
  /** Handle send to customer service action */
  onSendToCustomerService?: (order: Order) => void;
  
  /** Enable row selection */
  selectable?: boolean;
  
  /** Selected order IDs */
  selectedOrderIds?: string[];
  
  /** Handle selection change */
  onSelectionChange?: (orderIds: string[]) => void;
  
  /** Show actions column */
  showActions?: boolean;
  
  /** Column filters */
  columnFilters?: ColumnFilter[];
  
  /** Handle filter change */
  onFilterChange?: (filters: ColumnFilter[]) => void;
  
  /** Handle sort change */
  onSortChange?: (sort: ColumnSort | null) => void;
  
  /** Current sort */
  currentSort?: ColumnSort | null;
  
  /** Scroll container ID for filter dropdowns */
  scrollContainerId?: string;
}

/**
 * Styled components
 */
const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  background: white;
  border-radius: 12px;
  box-shadow: ${Shadows.sm};
`;

const Table = styled.table`
  width: 100%;
  min-width: 1180px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
`;

const TableHead = styled.thead`
  background-color: ${ThemeColors.background.tertiary};
`;

const TableRow = styled.tr<{ clickable?: boolean; selected?: boolean }>`
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  background-color: ${props => props.selected ? ThemeColors.primary.light : 'white'};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${props => props.selected 
      ? ThemeColors.primary.light 
      : ThemeColors.background.secondary};
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${ThemeColors.border.light};
  }
`;

const TableHeader = styled.th<{ width?: string }>`
  padding: ${Spacing.sm} ${Spacing.md};
  text-align: left;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: ${props => props.width || 'auto'};

  &:first-child {
    border-radius: 12px 0 0 0;
  }

  &:last-child {
    border-radius: 0 12px 0 0;
  }
`;

const TableCell = styled.td`
  padding: ${Spacing.sm} ${Spacing.md};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.primary};
  vertical-align: middle;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusBadge = styled.span<{ status: OrderStatus }>`
  display: inline-flex;
  align-items: center;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: 20px;
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  gap: ${Spacing.xs};
  background-color: ${props => {
    switch (props.status) {
      case 'approved': return ThemeColors.success.light;
      case 'pending': return ThemeColors.warning.light;
      default: return ThemeColors.background.tertiary;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'approved': return ThemeColors.success.dark;
      case 'pending': return ThemeColors.warning.dark;
      default: return ThemeColors.text.secondary;
    }
  }};

  svg {
    width: 14px;
    height: 14px;
  }
`;

/** Amount: fixed width, right-aligned — avoids overlap with actions */
const AmountTableCell = styled(TableCell)`
  width: 108px;
  min-width: 108px;
  max-width: 108px;
  text-align: right;
  box-sizing: border-box;
`;

/** Single kebab column — fixed narrow width */
const ActionsTableCell = styled(TableCell)`
  width: 48px;
  min-width: 48px;
  max-width: 48px;
  text-align: center;
  vertical-align: middle;
  padding-left: ${Spacing.sm};
  padding-right: ${Spacing.sm};
  overflow: visible;
  position: relative;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: ${ThemeColors.primary.main};
`;

const FlagIcon = styled(Flag)<{ flagged?: boolean }>`
  color: ${props => props.flagged ? ThemeColors.error.main : 'currentColor'};
`;

const EmptyState = styled.div`
  padding: ${Spacing.xxl} ${Spacing.lg};
  text-align: center;
  color: ${ThemeColors.text.secondary};
`;

const LoadingRow = styled(TableRow)`
  background: linear-gradient(
    90deg,
    ${ThemeColors.background.secondary} 25%,
    ${ThemeColors.background.tertiary} 50%,
    ${ThemeColors.background.secondary} 75%
  );
  background-size: 200% 100%;
  animation: loading 1.5s ease-in-out infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const AmountCell = styled.span`
  font-weight: ${Typography.fontWeight.semibold};
  color: ${ThemeColors.text.primary};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  display: block;
  width: 100%;
  text-align: right;
`;

const KebabToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${ThemeColors.text.secondary};
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${ThemeColors.background.secondary};
    color: ${ThemeColors.primary.main};
  }
`;

const ActionsDropdown = styled.div`
  position: fixed;
  z-index: 10000;
  min-width: 220px;
  max-width: min(280px, calc(100vw - 16px));
  background: white;
  border-radius: 10px;
  border: 1px solid ${ThemeColors.border.light};
  box-shadow: ${Shadows.md};
  padding: ${Spacing.xs} 0;
`;

const MenuRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border: none;
  background: none;
  cursor: pointer;
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.primary};
  text-align: left;

  &:hover {
    background: ${ThemeColors.background.secondary};
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

/**
 * Get status icon based on order status
 */
const getStatusIcon = (status: OrderStatus) => {
  switch (status) {
    case 'approved':
      return <CheckCircle />;
    case 'pending':
      return <Clock />;
    default:
      return <Clock />;
  }
};

/**
 * Format currency
 */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

/**
 * Format a date to YYYYMMDDHHMM timestamp format
 */
/** Milliseconds when CS email was sent, or 0 if never sent (for sorting). */
const getCustomerServiceEmailSentAtMs = (order: Order): number => {
  const v = order.customerServiceEmailSentAt;
  if (v == null) return 0;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isNaN(t) ? 0 : t;
  }
  try {
    const d = (v as Timestamp).toDate?.();
    return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0;
  } catch {
    return 0;
  }
};

const customerServiceEmailTooltip = (order: Order): string => {
  if (!order.customerServiceEmailSentAt) {
    return 'Not sent to Customer Service';
  }
  const v = order.customerServiceEmailSentAt;
  const d = v instanceof Date ? v : (v as Timestamp).toDate?.();
  if (!d || Number.isNaN(d.getTime())) {
    return 'Sent to Customer Service';
  }
  return `Sent to Customer Service: ${d.toLocaleString()}`;
};

const formatTimestamp = (date: Date | Timestamp | string | undefined): string | null => {
  if (!date) return null;
  
  let dateObj: Date;
  
  if (date instanceof Date) {
    dateObj = date;
  } else if (typeof date === 'string') {
    const localYmd = parseCalendarYMDToLocalDate(date.trim());
    dateObj = localYmd ?? new Date(date);
  } else if (date && typeof date === 'object' && 'toDate' in date) {
    // Firestore Timestamp
    dateObj = (date as any).toDate();
  } else {
    return null;
  }
  
  if (isNaN(dateObj.getTime())) {
    return null;
  }
  
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hours = String(dateObj.getHours()).padStart(2, '0');
  const minutes = String(dateObj.getMinutes()).padStart(2, '0');
  
  return `${year}${month}${day}${hours}${minutes}`;
};

/**
 * Retrieve the order identifier to display.
 * Always use orderId (which should be in YYYYMMDDHHMM timestamp format).
 * PO number is stored separately but orderId is the primary identifier.
 */
const getOrderIdentifier = (order: Order): string => {
  // Always use orderId as the primary identifier (should be timestamp format)
  if (order.orderId && /^\d{12}$/.test(order.orderId)) {
    return order.orderId;
  }
  
  // If orderId is not in timestamp format, try to format from order date
  const timestamp = formatTimestamp(
    order.finalizedAt || order.orderDate || order.createdAt || order.submittedAt
  );
  
  if (timestamp) {
    return timestamp;
  }
  
  // Last resort: use orderId as-is or format from it
  if (order.orderId) {
    const dateFromId = new Date(order.orderId);
    if (!isNaN(dateFromId.getTime())) {
      const formatted = formatTimestamp(dateFromId);
      if (formatted) return formatted;
    }
    return order.orderId;
  }
  
  return 'N/A';
};

/**
 * Format order identifier for display.
 * For timestamp format (YYYYMMDDHHMM), show it fully.
 * For other identifiers, truncate if needed.
 */
const formatOrderIdentifier = (identifier?: string, maxLength: number = 12): string => {
  if (!identifier) {
    return 'N/A';
  }

  // If it's a timestamp format (12 digits), show it fully
  if (/^\d{12}$/.test(identifier)) {
    return identifier;
  }

  // For other identifiers, truncate if needed
  return identifier.length > maxLength
    ? `${identifier.substring(0, maxLength)}...`
    : identifier;
};

const orderRowKey = (order: Order) => `${order.storeId}__${order.orderId}`;

interface OrderRowActionsMenuProps {
  order: Order;
  rowKey: string;
  isOpen: boolean;
  onToggle: (key: string | null) => void;
  onView?: (order: Order) => void;
  onDownloadPDF?: (order: Order) => void;
  onDownloadCSV?: (order: Order) => void;
  onApprove?: (order: Order) => void;
  onEdit?: (order: Order) => void;
  onSendToCustomerService?: (order: Order) => void;
  onFlag?: (order: Order) => void;
  onDelete?: (order: Order) => void;
}

const OrderRowActionsMenu: React.FC<OrderRowActionsMenuProps> = ({
  order,
  rowKey,
  isOpen,
  onToggle,
  onView,
  onDownloadPDF,
  onDownloadCSV,
  onApprove,
  onEdit,
  onSendToCustomerService,
  onFlag,
  onDelete
}) => {
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const panelW = 228;
    const left = Math.max(8, Math.min(r.right - panelW, window.innerWidth - panelW - 8));
    setPos({ top: r.bottom + 4, left });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onScroll = () => updatePosition();
    const onResize = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      onToggle(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [isOpen, onToggle]);

  const close = () => onToggle(null);

  const run = (fn?: (o: Order) => void) => {
    if (fn) fn(order);
    close();
  };

  return (
    <div
      data-order-kebab-root
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'inline-flex', justifyContent: 'center', width: '100%' }}
    >
      <KebabToggle
        ref={btnRef}
        type="button"
        title="Actions"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        onClick={(e) => {
          e.stopPropagation();
          onToggle(isOpen ? null : rowKey);
        }}
      >
        <MoreVertical size={18} strokeWidth={2} />
      </KebabToggle>
      {isOpen &&
        createPortal(
          <ActionsDropdown
            ref={panelRef}
            role="menu"
            style={{ top: pos.top, left: pos.left }}
          >
            {onView && (
              <MenuRow type="button" role="menuitem" onClick={() => run(onView)}>
                <Eye />
                View details
              </MenuRow>
            )}
            {onDownloadPDF && (
              <MenuRow type="button" role="menuitem" onClick={() => run(onDownloadPDF)}>
                <FileText style={{ color: ThemeColors.error.main }} />
                Download PDF
              </MenuRow>
            )}
            {onDownloadCSV && (
              <MenuRow type="button" role="menuitem" onClick={() => run(onDownloadCSV)}>
                <FileSpreadsheet style={{ color: ThemeColors.success.main }} />
                Download CSV
              </MenuRow>
            )}
            {onApprove && order.status === 'pending' && (
              <MenuRow type="button" role="menuitem" onClick={() => run(onApprove)}>
                <CheckCircle style={{ color: ThemeColors.success.main }} />
                Approve order
              </MenuRow>
            )}
            {onEdit && (
              <MenuRow type="button" role="menuitem" onClick={() => run(onEdit)}>
                <Edit style={{ color: ThemeColors.info.main }} />
                Edit order
              </MenuRow>
            )}
            {onSendToCustomerService && order.status === 'approved' && !order.customerServiceEmailSentAt && (
              <MenuRow type="button" role="menuitem" onClick={() => run(onSendToCustomerService)}>
                <Send style={{ color: ThemeColors.primary.main }} />
                Send to customer service
              </MenuRow>
            )}
            {onFlag && (
              <MenuRow type="button" role="menuitem" onClick={() => run(onFlag)}>
                <FlagIcon size={16} flagged={order.flaggedForReview} />
                {order.flaggedForReview ? 'Unflag order' : 'Flag for review'}
              </MenuRow>
            )}
            {onDelete && (
              <MenuRow type="button" role="menuitem" onClick={() => run(onDelete)}>
                <Trash2 style={{ color: ThemeColors.error.main }} />
                Delete order
              </MenuRow>
            )}
          </ActionsDropdown>,
          document.body
        )}
    </div>
  );
};

/**
 * OrderTable Component
 * 
 * @example
 * ```tsx
 * <OrderTable
 *   orders={orders}
 *   isLoading={isLoading}
 *   onRowClick={(order) => navigate(`/order/${order.orderId}`)}
 *   onDownloadPDF={handleDownloadPDF}
 *   onDownloadCSV={handleDownloadCSV}
 *   showActions
 * />
 * ```
 */
export const OrderTable: React.FC<OrderTableProps> = ({
  orders,
  isLoading = false,
  onRowClick,
  onDownloadPDF,
  onDownloadCSV,
  onView,
  onFlag,
  onApprove,
  onEdit,
  onDelete,
  onSendToCustomerService,
  selectable = false,
  selectedOrderIds = [],
  onSelectionChange,
  showActions = true,
  columnFilters = [],
  onFilterChange,
  onSortChange,
  currentSort,
  scrollContainerId = 'order-table-scroll'
}) => {
  const [localSelectedIds, setLocalSelectedIds] = useState<string[]>(selectedOrderIds);
  const [openActionsKey, setOpenActionsKey] = useState<string | null>(null);

  /**
   * Handle row selection
   */
  const handleRowSelect = (orderId: string, checked: boolean) => {
    const newSelection = checked
      ? [...localSelectedIds, orderId]
      : localSelectedIds.filter(id => id !== orderId);
    
    setLocalSelectedIds(newSelection);
    onSelectionChange?.(newSelection);
  };

  /**
   * Prepare data for filtering and sorting
   */
  const tableData = useMemo(() => {
    return orders.map(order => ({
      orderId: getOrderIdentifier(order),
      customerNumber: String(order.customerNumber || '').trim(),
      storeName: order.storeName || '',
      salespersonNo: String(order.salespersonNo || order.salesRepId || '').trim(),
      salesRepName: String(order.salesRepName || '').trim(),
      city: order.city || '',
      province: order.province || '',
      orderType: order.orderType || '',
      orderDate: order.orderDate || '',
      status: order.status || '',
      totalItems: order.totalItems || 0,
      totalAmount: order.totalAmount || 0,
      csEmailSent: order.customerServiceEmailSentAt ? 'Sent' : 'Not sent',
      // Keep original order for row click
      _originalOrder: order
    }));
  }, [orders]);

  /**
   * Apply filters and sorting to orders
   */
  const filteredAndSortedOrders = useMemo(() => {
    let filtered = [...orders];

    // Apply column filters
    if (columnFilters.length > 0) {
      filtered = filtered.filter(order => {
        return columnFilters.every(filter => {
          if (!filter.value) return true;
          
          // Handle both single value and array of values (multi-select)
          const filterValues = Array.isArray(filter.value) 
            ? filter.value.map(v => v.toLowerCase())
            : [filter.value.toLowerCase()];
          
          switch (filter.column) {
            case 'orderId':
              const orderId = getOrderIdentifier(order)?.toLowerCase() || '';
              return filterValues.some(v => orderId.includes(v));
            case 'customerNumber':
              const cust = String(order.customerNumber || '')
                .trim()
                .toLowerCase();
              return filterValues.some((v) => cust.includes(v) || cust === v);
            case 'storeName':
              const storeName = (order.storeName || '').toLowerCase();
              return filterValues.some(v => storeName.includes(v));
            case 'salespersonNo':
              const salesperson = String(order.salespersonNo || order.salesRepId || '').trim().toLowerCase();
              return filterValues.some(v => salesperson.includes(v) || salesperson === v);
            case 'salesRepName':
              const repName = String(order.salesRepName || '').trim().toLowerCase();
              return filterValues.some(v => repName.includes(v) || repName === v);
            case 'city':
              const city = (order.city || '').toLowerCase();
              return filterValues.some(v => city.includes(v) || city === v);
            case 'province':
              const province = (order.province || '').toLowerCase();
              return filterValues.some(v => province.includes(v) || province === v);
            case 'orderType':
              const orderType = (order.orderType || '').toLowerCase();
              return filterValues.some(v => orderType.includes(v) || orderType === v);
            case 'status':
              const status = (order.status || '').toLowerCase();
              return filterValues.some(v => status.includes(v) || status === v);
            case 'csEmailSent': {
              const sent = !!order.customerServiceEmailSentAt;
              return filterValues.some((v) => {
                const x = v.toLowerCase();
                return (
                  (sent && (x === 'sent' || x === 'yes' || x === 'true')) ||
                  (!sent && (x === 'not sent' || x === 'no' || x === 'false'))
                );
              });
            }
            default:
              return true;
          }
        });
      });
    }

    // Apply sorting
    if (currentSort && currentSort.column && currentSort.direction) {
      filtered.sort((a, b) => {
        let aVal: any;
        let bVal: any;

        switch (currentSort.column) {
          case 'orderId':
            aVal = getOrderIdentifier(a);
            bVal = getOrderIdentifier(b);
            break;
          case 'customerNumber':
            aVal = String(a.customerNumber || '').trim();
            bVal = String(b.customerNumber || '').trim();
            break;
          case 'storeName':
            aVal = a.storeName || '';
            bVal = b.storeName || '';
            break;
          case 'salespersonNo':
            aVal = String(a.salespersonNo || a.salesRepId || '').trim();
            bVal = String(b.salespersonNo || b.salesRepId || '').trim();
            break;
          case 'salesRepName':
            aVal = String(a.salesRepName || '').trim();
            bVal = String(b.salesRepName || '').trim();
            break;
          case 'city':
            aVal = a.city || '';
            bVal = b.city || '';
            break;
          case 'province':
            aVal = a.province || '';
            bVal = b.province || '';
            break;
          case 'orderDate':
            aVal = orderDateToLocalSortable(a.orderDate);
            bVal = orderDateToLocalSortable(b.orderDate);
            break;
          case 'totalItems':
            aVal = a.totalItems || 0;
            bVal = b.totalItems || 0;
            break;
          case 'totalAmount':
            aVal = a.totalAmount || 0;
            bVal = b.totalAmount || 0;
            break;
          case 'csEmailSent':
            aVal = getCustomerServiceEmailSentAtMs(a);
            bVal = getCustomerServiceEmailSentAtMs(b);
            break;
          default:
            return 0;
        }

        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return currentSort.direction === 'asc' 
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        return currentSort.direction === 'asc' 
          ? (aVal > bVal ? 1 : aVal < bVal ? -1 : 0)
          : (aVal < bVal ? 1 : aVal > bVal ? -1 : 0);
      });
    }

    return filtered;
  }, [orders, columnFilters, currentSort]);

  /**
   * Handle select all - selects only visible/filtered orders
   */
  const handleSelectAll = useCallback((checked: boolean) => {
    // Use filteredAndSortedOrders for select all
    const ordersToSelect = checked ? filteredAndSortedOrders.map(o => o.orderId) : [];
    setLocalSelectedIds(ordersToSelect);
    onSelectionChange?.(ordersToSelect);
  }, [filteredAndSortedOrders, onSelectionChange]);

  /**
   * Check if all visible rows are selected
   */
  const allSelected = useMemo(() => {
    return filteredAndSortedOrders.length > 0 && 
           filteredAndSortedOrders.every(order => localSelectedIds.includes(order.orderId));
  }, [filteredAndSortedOrders, localSelectedIds]);

  if (isLoading) {
    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {selectable && <TableHeader>Select</TableHeader>}
              <TableHeader>Order ID</TableHeader>
              <TableHeader>Customer #</TableHeader>
              <TableHeader>Store</TableHeader>
              <TableHeader>Date</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Items</TableHeader>
              <TableHeader>Amount</TableHeader>
              {showActions && <TableHeader>Actions</TableHeader>}
            </TableRow>
          </TableHead>
          <tbody>
            {[1, 2, 3, 4, 5].map((i) => (
              <LoadingRow key={i}>
                {selectable && <TableCell></TableCell>}
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                {showActions && <TableCell></TableCell>}
              </LoadingRow>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    );
  }

  if (orders.length === 0) {
    return (
      <TableContainer>
        <EmptyState>
          <p>No orders found</p>
        </EmptyState>
      </TableContainer>
    );
  }

  return (
    <TableContainer id={scrollContainerId}>
      <Table>
        <TableHead>
          <TableRow>
            {selectable && (
              <TableHeader width="40px">
                <Checkbox
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableHeader>
            )}
            <SortableFilterableTableHeader
              scrollContainerId={scrollContainerId}
              columns={[
                { key: 'orderId', label: 'Order ID', filterable: true, sortable: true },
                { key: 'customerNumber', label: 'Customer #', filterable: true, sortable: true },
                { key: 'storeName', label: 'Store', filterable: true, sortable: true },
                { key: 'salespersonNo', label: 'Salesperson No', filterable: true, sortable: true, dbField: 'SalespersonNo' },
                { key: 'salesRepName', label: 'Sales Rep', filterable: true, sortable: true },
                { key: 'city', label: 'City', filterable: true, sortable: true },
                { key: 'province', label: 'Province', filterable: true, sortable: true },
                { key: 'orderType', label: 'Order Type', filterable: true, sortable: true },
                { key: 'orderDate', label: 'Date', filterable: false, sortable: true },
                { key: 'status', label: 'Status', filterable: true, sortable: true },
                {
                  key: 'csEmailSent',
                  label: 'CS Email',
                  filterable: true,
                  sortable: true
                },
                { key: 'totalItems', label: 'Items', filterable: false, sortable: true },
                { key: 'totalAmount', label: 'Amount', filterable: false, sortable: true },
                ...(showActions ? [{ key: 'actions', label: 'Actions', filterable: false, sortable: false }] : [])
              ]}
              data={tableData}
              onFilterChange={onFilterChange || (() => {})}
              onSortChange={onSortChange || (() => {})}
              currentFilters={columnFilters}
              currentSort={currentSort}
              renderAsRow={true}
            />
          </TableRow>
        </TableHead>
        <tbody>
          {filteredAndSortedOrders.map((order) => {
            const rawIdentifier = getOrderIdentifier(order);

            return (
            <TableRow
              key={orderRowKey(order)}
              clickable={true}
              selected={localSelectedIds.includes(order.orderId)}
              onClick={(e) => {
                const target = e.target as HTMLElement;
                const isCheckbox = target.closest('input[type="checkbox"]');
                const isKebab = target.closest('[data-order-kebab-root]');
                if (!isCheckbox && !isKebab) {
                  onRowClick?.(order);
                }
              }}
            >
              {selectable && (
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    type="checkbox"
                    checked={localSelectedIds.includes(order.orderId)}
                    onChange={(e) => handleRowSelect(order.orderId, e.target.checked)}
                  />
                </TableCell>
              )}
              <TableCell>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span title={rawIdentifier || 'N/A'} style={{ fontFamily: 'monospace', fontSize: Typography.fontSize.xs }}>
                    {formatOrderIdentifier(rawIdentifier)}
                  </span>
                  {order.flaggedForReview && (
                    <FlagIcon size={12} flagged />
                  )}
                </div>
              </TableCell>
              <TableCell title={order.customerNumber || 'N/A'}>
                <span style={{ fontFamily: 'monospace', fontSize: Typography.fontSize.xs }}>
                  {order.customerNumber?.trim() || 'N/A'}
                </span>
              </TableCell>
              <TableCell>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }} title={order.storeName || 'N/A'}>
                  {order.storeName || 'N/A'}
                </div>
              </TableCell>
              <TableCell>{order.salespersonNo || 'N/A'}</TableCell>
              <TableCell title={order.salesRepName || 'N/A'}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '140px' }}>
                  {order.salesRepName || 'N/A'}
                </div>
              </TableCell>
              <TableCell>{order.city || 'N/A'}</TableCell>
              <TableCell>{order.province || 'N/A'}</TableCell>
              <TableCell>
                <span style={{
                  padding: `${Spacing.xs} ${Spacing.sm}`,
                  borderRadius: '4px',
                  backgroundColor: order.orderType === 'custOrder' ? '#e3f2fd' : '#fff3e0',
                  color: order.orderType === 'custOrder' ? '#1976d2' : '#f57c00',
                  fontWeight: Typography.fontWeight.medium,
                  fontSize: Typography.fontSize.xs,
                  textTransform: 'uppercase'
                }}>
                  {order.orderType === 'custOrder' ? 'Cust Order' : order.orderType === 'offCarDelivery' ? 'Off Car' : 'N/A'}
                </span>
              </TableCell>
              <TableCell>{formatOrderDateForDisplay(order.orderDate)}</TableCell>
              <TableCell>
                <StatusBadge status={order.status}>
                  {getStatusIcon(order.status)}
                  {order.status}
                </StatusBadge>
              </TableCell>
              <TableCell title={customerServiceEmailTooltip(order)}>
                {order.customerServiceEmailSentAt ? (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: `${Spacing.xs} ${Spacing.sm}`,
                      borderRadius: 6,
                      backgroundColor: '#e8f5e9',
                      color: ThemeColors.success.dark,
                      fontWeight: Typography.fontWeight.medium,
                      fontSize: Typography.fontSize.xs
                    }}
                  >
                    <Send size={12} aria-hidden />
                    Sent
                  </span>
                ) : (
                  <span
                    style={{
                      color: ThemeColors.text.secondary,
                      fontSize: Typography.fontSize.xs
                    }}
                  >
                    Not sent
                  </span>
                )}
              </TableCell>
              <TableCell>{order.totalItems || 0}</TableCell>
              <AmountTableCell>
                <AmountCell title={formatCurrency(order.totalAmount || 0)}>
                  {formatCurrency(order.totalAmount || 0)}
                </AmountCell>
              </AmountTableCell>
              {showActions && (
                <ActionsTableCell>
                  <OrderRowActionsMenu
                    order={order}
                    rowKey={orderRowKey(order)}
                    isOpen={openActionsKey === orderRowKey(order)}
                    onToggle={setOpenActionsKey}
                    onView={onView}
                    onDownloadPDF={onDownloadPDF}
                    onDownloadCSV={onDownloadCSV}
                    onApprove={onApprove}
                    onEdit={onEdit}
                    onSendToCustomerService={onSendToCustomerService}
                    onFlag={onFlag}
                    onDelete={onDelete}
                  />
                </ActionsTableCell>
              )}
            </TableRow>
            );
          })}
        </tbody>
      </Table>
    </TableContainer>
  );
};

